import { ID } from '../../types/UtilityTypes'
import { createAsyncAction, createReducer, isActionOf } from 'typesafe-actions'
import { mergeReducer } from '../../utils/redux-utils'
import { filter, map, mergeMap } from 'rxjs/operators'
import { apiRequests } from '../../http/config'
import { defaultValueW, modifyProp, snoc } from '../../utils/utils'
import { combineEpics } from 'redux-observable'
import { entityRemove, eqEntity } from '../../utils/entity-utils'
import { toastFailedResponse } from '../../components/controls/Toaster/Toaster'
import { Tag } from '../../responses/tags'
import { flow } from 'fp-ts/es6/function'
import * as A from 'fp-ts/es6/Array'
import * as R from 'fp-ts/es6/Record'
import { RootEpic } from '../root'
import { projectActions } from './project/project-actions'
import { getLastSemigroup } from 'fp-ts/es6/Semigroup'

const actions = {
    add: createAsyncAction(
        '@DATA_TAG/ADD_REQUEST',
        '@DATA_TAG/ADD_SUCCESS',
        '@DATA_TAG/ADD_FAILURE',
    )<{ objectId: ID, tag: string }, { objectId: ID, tag: Tag }, void>(),
    list: createAsyncAction(
        '@DATA_TAG/LIST_REQUEST',
        '@DATA_TAG/LIST_SUCCESS',
        '@DATA_TAG/LIST_FAILURE',
    )<ID, Record<ID, Tag[]>, void>(),
    delete: createAsyncAction(
        '@DATA_TAG/DELETE_REQUEST',
        '@DATA_TAG/DELETE_SUCCESS',
        '@DATA_TAG/DELETE_FAILURE',
    )<{ objectId: ID, tag: Tag }, { objectId: ID, tag: Tag }, void>(),
}

export { actions as tagActions }

export type TagState = Record<ID, Tag[]>

export const tagReducer = createReducer<TagState>({})
    .handleAction(projectActions.get.success, (state, action) => ({
        ...state,
        ...R.fromFoldableMap(getLastSemigroup<Tag[]>(), A.array)(
            action.payload.project_work_objects,
            object => [object.id.toString(), object.tags],
        )
    }))
    .handleAction(actions.list.success, mergeReducer)
    .handleAction(actions.add.success, (state, { payload }) =>
        modifyProp<TagState, ID>(payload.objectId, flow(
            defaultValueW([]),
            snoc(payload.tag),
            A.uniq<Tag>(eqEntity)
        ))(state))
    .handleAction(actions.delete.success, (state, { payload }) =>
        modifyProp<TagState, ID>(payload.objectId, entityRemove(payload.tag))(state))

const add: RootEpic = action$ => action$.pipe(
    filter(isActionOf(actions.add.request)),
    mergeMap(({ payload }) => apiRequests.project.tags.add({
        params: { objectId: payload.objectId },
        data: { name: payload.tag },
    }).pipe(
        map((res) => res.response.success ?
            actions.add.success({ objectId: payload.objectId, tag: res.response.row }) :
            actions.add.failure(toastFailedResponse(res.response, 'Не удалось добавить тег'))
        ),
    )),
)

const list: RootEpic = action$ => action$.pipe(
    filter(isActionOf(actions.list.request)),
    mergeMap(action => apiRequests.project.tags.list({
        params: { objectId: action.payload },
    }).pipe(
        map(res => res.response.success?
            actions.list.success({ [action.payload]: res.response.rows }):
            actions.list.failure(toastFailedResponse(res.response, 'Не удалось получить список тегов'))
        )
    ))
)

const remove: RootEpic = action$ => action$.pipe(
    filter(isActionOf(actions.delete.request)),
    mergeMap(({ payload }) => apiRequests.project.tags.delete({
        params: { objectId: payload.objectId, tagId: payload.tag.id }
    }).pipe(
        map((res) => res.response.success ?
            actions.delete.success(payload) :
            actions.delete.failure(toastFailedResponse(res.response, 'Не удалось удалить тег'))
        )
    )),
)

export const tagEpic = combineEpics(add, list, remove)